import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import MainHeading from '@/components/common/MainHeading';
import Layout from '@/components/Layout/Layout';
import { RoutesLink } from '@/utility/RouteLinks';
import { IFormData, IFormRowData, FormType } from '@/interfaces/form.model';
import FilterComponent, {
  IFilterArray,
} from '@/components/common/FilterComponent';
import CommonTable, {
  ITableColumn,
  IActionData,
} from '@/components/common/CommonTable';
import CardLayout from '@/components/Layout/CardLayout';
import { FormService } from '@/services/FormService';
import ViewIcon from '@/components/Icons/ViewIcon';
import moment from 'moment';
import { showToastNotification } from '@/utility/helper';
import { setUpdateFilterStates } from '@/redux/features/UserSlice';
import { RootState } from '@/redux/store';

interface IPageStates {
  tableLoader: boolean;
  searchQuery: string;
}

const TeamSubmissionForm = (): React.ReactNode => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState<IFormRowData[]>([]);
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );

  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  const [pageStates, setPageStates] = useState<IPageStates>({
    tableLoader: false,
    searchQuery: '',
  });

  const fetchSubmissionFormsData = useCallback(async () => {
    setPageStates((prev: IPageStates) => ({ ...prev, tableLoader: true }));
    try {
      const filter = {
        pageNumber: filterDetails.currentPage,
        pageSize: filterDetails.pageSize,
        search: pageStates.searchQuery,
        orderBy: filterDetails.orderBy,
      };

      const response = await FormService.getTeamsFormSubmission(id, filter);
      console.log(response, 'Teamresponse');

      if (response?.status) {
        const forms = response.data?.officeForm || [];
        const newData = forms?.map((officeForm: IFormData, idx: number) => ({
          ...officeForm,
          sno:
            (filterDetails.currentPage - 1) * (filterDetails.pageSize || 10) +
            idx +
            1,
          // Ensure basic fields are present for processedData mapping later
          name: officeForm?.form?.name ?? '--',
          submittedByName: officeForm?.submittedBy?.name ?? '--',
          description: officeForm?.description ?? '--',
          totalColumns: officeForm?.data?.length ?? 0,
          status: officeForm?.status ?? '--',
          createdAt: officeForm?.createdAt ?? '--',
        }));
        setTableData(newData as IFormRowData[]);
        dispatch(
          setUpdateFilterStates({
            count: response.data?.pageInfo?.total ?? 0,
            totalPages: response.data?.pageInfo?.totalPages ?? 1,
          }),
        );
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Failed to fetch forms',
        );
      }
    } catch (error: unknown) {
      showToastNotification(
        'danger',
        (error as Error)?.message || 'Error fetching forms',
      );
    } finally {
      setPageStates((prev: IPageStates) => ({ ...prev, tableLoader: false }));
    }
  }, [
    filterDetails.currentPage,
    filterDetails.pageSize,
    filterDetails.orderBy,
    pageStates.searchQuery,
    dispatch,
    id,
  ]);

  useEffect(() => {
    fetchSubmissionFormsData();
  }, [id, fetchSubmissionFormsData]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    timeoutId.current = setTimeout(() => {
      dispatch(setUpdateFilterStates({ currentPage: 1 }));
      setPageStates((prevState: IPageStates) => ({
        ...prevState,
        searchQuery: value,
      }));
    }, 500);
  };

  const pageChangeHandler = (PageNo: number) => {
    if (PageNo > 0 && PageNo <= filterDetails?.totalPages) {
      dispatch(setUpdateFilterStates({ currentPage: PageNo }));
    }
  };

  const handleChangePageSize = (value: number) => {
    dispatch(setUpdateFilterStates({ pageSize: value, currentPage: 1 }));
  };

  const tableHeading: ITableColumn<IFormRowData>[] = [
    { label: 'S.No.', value: 'sno' },
    { label: 'Form Name', value: 'name' },
    { label: 'Submitted By', value: 'submittedByName' },
    // { label: 'Description', value: 'description' },
    { label: 'Total Columns', value: 'totalColumns' },
    { label: 'Status', value: 'formStatus' },
    { label: 'Created At', value: 'formCreatedAt' },
  ];

  const actionData: IActionData<IFormRowData>[] = [
    {
      label: 'View',
      className: 'btn btn-outline-secondary p-1 border-0',
      icon: <ViewIcon />,
      title: 'View',
      clickHandler: (data) =>
        navigate(
          `${RoutesLink.formView}/${data.formId}?userId=${data.submittedBy?.id}`,
          {
            state: {
              id: data?.id,
              formType: FormType.TeamSubmission,
              isShow: true,
            },
          },
        ),
    },
  ];

  const filterArray: IFilterArray<boolean> = {
    isShow: false,
  };

  // Convert status to badge styling and format date
  const processedData: IFormRowData[] = tableData?.map((item) => {
    // Extract status string safely if it's an object
    const statusStr = item?.status;
    // console.log("statusStr", statusStr);

    let statusClass = 'bg-secondary';
    const lowerStatus = String(statusStr || '').toLowerCase();

    if (lowerStatus === 'published' || lowerStatus === 'active')
      statusClass = 'bg-success';
    if (lowerStatus === 'draft' || lowerStatus === 'pending')
      statusClass = 'bg-warning text-dark';
    if (lowerStatus === 'submitted') statusClass = 'bg-primary';

    return {
      ...item,
      formStatus: (
        <span
          className={`badge ${statusClass} fw-normal rounded-pill py-1 fs-6`}
        >
          {statusStr ? String(statusStr)?.toUpperCase() : 'N/A'}
        </span>
      ),
      formCreatedAt: item?.createdAt
        ? moment(item?.createdAt).format('DD MMM YYYY, hh:mm A')
        : '--',
    };
  });

  return (
    <Layout>
      <MainHeading title="Teams Forms" showbBackButton={true} />
      <CardLayout>
        <FilterComponent
          searchBar={true}
          searchPlaceHolder="Search Forms"
          handleSearch={handleSearchChange}
          filterArray={filterArray}
        />
        <CommonTable
          tableData={processedData}
          tableHeading={tableHeading}
          count={filterDetails.count}
          pageSize={filterDetails.pageSize || 10}
          currentPage={filterDetails.currentPage}
          totalPages={filterDetails.totalPages}
          pageChangeHandler={pageChangeHandler}
          handleChangePageSize={handleChangePageSize}
          isLoading={pageStates.tableLoader}
          ActionData={actionData}
        />
      </CardLayout>
    </Layout>
  );
};

export default TeamSubmissionForm;
